/**
 * OF-001D #3 — Canonical Role Label Resolver (FE SSOT).
 *
 * Single shared resolver for every role label rendered anywhere in
 * the frontend.  All ACTIVE UI surfaces must consume `roleLabel()`
 * from this module — NO component, context, or page may define its
 * own provider/internal role label map.
 *
 * Labels here are byte-identical to the backend `WORKSPACE_CATALOG`
 * display strings (`backend/services/unified_auth_service.py`) so the
 * "what you invite as" / "what the workspace switcher shows" /
 * "what the header badge prints" / "what the message tag reads" all
 * resolve to the same string for the same canonical role_id.
 *
 * Drift-guarded by:
 *   backend/tests/test_of001d_canonical_role_label_resolver.py
 *
 * Charter — OF-001D #3 §1/§2/§8 hard rules:
 *   - Forbidden FE literals in active runtime: 'Sales Rep',
 *     'Patient Coordinator', generic 'Coordinator',
 *     'provider_patient_coordinator' (phantom).
 *   - Allowed only in comments / markdown / explicitly-tagged
 *     regression fixtures.
 *   - Provider role family ≠ TreatX internal role family.  Tenant
 *     scopes stay separate; only the vocabulary infrastructure is shared.
 */

import {
  PROVIDER_ADMIN, PROVIDER_SALES_REP, PROVIDER_COORDINATOR,
  PROVIDER_ACCOUNTANT, PROVIDER_AFTER_SALES_REP, PROVIDER_DOCTOR,
  PROVIDER_STAFF,
  TREATX_SUPER_ADMIN, TREATX_ADMIN, TREATX_COORDINATOR,
  TREATX_SALES_REP, TREATX_AFTER_SALES_REP, TREATX_ACCOUNTANT,
  TREATX_SUPPORT,
  PATIENT, API_CLIENT, PENDING,
} from './canonicalRoles';

// ─────────────────────── Provider canonical labels ─────────────────────────

export const PROVIDER_ROLE_LABELS = {
  [PROVIDER_ADMIN]:           'Clinic Admin',
  [PROVIDER_SALES_REP]:       'Sales',
  [PROVIDER_COORDINATOR]:     'Operations',
  [PROVIDER_ACCOUNTANT]:      'Accounting',
  [PROVIDER_AFTER_SALES_REP]: 'After-Sales',
  [PROVIDER_DOCTOR]:          'Doctor',
  [PROVIDER_STAFF]:           'Staff',
};

// Provider roles eligible for Team Invite (Doctor onboards via
// Doctor Affiliations; Staff is dormant).
export const INVITE_ALLOWED_PROVIDER_ROLES = [
  PROVIDER_ADMIN,
  PROVIDER_SALES_REP,
  PROVIDER_COORDINATOR,
  PROVIDER_ACCOUNTANT,
  PROVIDER_AFTER_SALES_REP,
];

// Provider roles that own a workspace + dashboard route.
export const WORKSPACE_CAPABLE_PROVIDER_ROLES = [
  PROVIDER_ADMIN,
  PROVIDER_SALES_REP,
  PROVIDER_COORDINATOR,
  PROVIDER_ACCOUNTANT,
  PROVIDER_AFTER_SALES_REP,
];

// ─────────────────────── TreatX internal labels ────────────────────────────

export const TREATX_ROLE_LABELS = {
  [TREATX_SUPER_ADMIN]:       'TreatX Super Admin',
  [TREATX_ADMIN]:             'TreatX Admin',
  [TREATX_COORDINATOR]:       'TreatX Operations',
  [TREATX_SALES_REP]:         'TreatX Sales',
  [TREATX_AFTER_SALES_REP]:   'TreatX After-Sales',
  [TREATX_ACCOUNTANT]:        'TreatX Accounting',
  [TREATX_SUPPORT]:           'TreatX Support',
};

// ─────────────────────── System / patient labels ───────────────────────────

export const SYSTEM_ROLE_LABELS = {
  [PATIENT]:    'Patient',
  [API_CLIENT]: 'API Client',
  [PENDING]:    'Pending',
};

// ─────────────────────── Resolver ──────────────────────────────────────────

/**
 * Return the canonical display label for a role_id.  Falls back to a
 * humanised version of the id if unknown (never returns null).
 */
export function roleLabel(roleId) {
  if (!roleId || typeof roleId !== 'string') return '—';
  return (
    PROVIDER_ROLE_LABELS[roleId] ||
    TREATX_ROLE_LABELS[roleId] ||
    SYSTEM_ROLE_LABELS[roleId] ||
    // Humanised fallback for unknown roles (e.g. forward-compatible
    // names) — strip prefix and title-case.
    roleId
      .replace(/^(provider_|treatx_)/, '')
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ')
  );
}

/**
 * Resolve a role_id label, with an explicit fallback for
 * non-canonical inputs (used by surfaces that historically rendered
 * "—" or "Unknown" — keep their existing UX wording).
 */
export function roleLabelOr(roleId, fallback) {
  if (!roleId) return fallback;
  const hit =
    PROVIDER_ROLE_LABELS[roleId] ||
    TREATX_ROLE_LABELS[roleId] ||
    SYSTEM_ROLE_LABELS[roleId];
  return hit || fallback;
}

/** Convenience: every canonical role_id (used by drift guards). */
export const ALL_CANONICAL_ROLE_LABELS = {
  ...PROVIDER_ROLE_LABELS,
  ...TREATX_ROLE_LABELS,
  ...SYSTEM_ROLE_LABELS,
};
